import React from "react";
import { Handle, Position } from "reactflow";
import {
    BodySecondary,
    COLORS,
    Avatar,
    AVATAR_TYPES,
    SIZE,
    AVATAR_BACKGROUND,
    BodyCaption,
} from "@bikdotai/bik-component-library";
import { CustomNodeProps, IconMapping } from "../../../utilities/flowBuilder.utility";
import { NodeStyle } from "../../flow-builder/style";
import { ActionNodeStyle } from "../../flow-builder/actionNode/style";
import { backgroundBasedOnSubType } from "../../flow-builder/util";
import { AddActionButton } from "../../../app/action-block/ticketActions/ticketActions.styled";
import { NodeSubType } from "../../../models/nodeSubType";
import { NodeIdHeader } from "../../flow-builder/messageNode/NodeIdHeader";
import {
    ActionDescriptionRow,
    ActionSeparator,
    ChipTag,
    PriorityDot,
} from "./style";

const getPriorityColor = (val: string): string => {
    switch (val.toUpperCase()) {
        case "URGENT": return "#D32F2F";
        case "HIGH":   return "#FFB300";
        case "MEDIUM": return "#1976D2";
        case "LOW":    return "#388E3C";
        default:       return "#9E9E9E";
    }
};

const truncate = (text: string, maxLen: number): string =>
    text.length > maxLen ? text.substring(0, maxLen) + "..." : text;

interface TagValueListProps {
    values: string[];
    maxChars: number;
}

const TagValueList = ({ values, maxChars }: TagValueListProps) => (
    <div className="flex align-items-center" style={{ gap: "4px" }}>
        <ChipTag>
            <BodySecondary
                color={COLORS.content.secondary}
                style={{ fontWeight: 500, fontSize: "14px" }}
            >
                {truncate(values[0] ?? "", maxChars)}
            </BodySecondary>
        </ChipTag>
        {values.length > 1 && (
            <BodySecondary color={COLORS.content.secondary} style={{ fontSize: "14px" }}>
                {`+${values.length - 1}`}
            </BodySecondary>
        )}
    </div>
);

/**
 * MacroMultiActionNode
 *
 * A dedicated canvas node for the Macros module that renders MULTI_ACTION nodes
 * (ticket actions). Unlike the general ActionBlockNode, this component is specific
 * to the macro builder context and uses NodeSubType.MULTI_ACTION.
 */
const MacroMultiActionNode = (props: CustomNodeProps) => {
    const {
        nodeData,
        leftHandlerStyle,
        rightHandlerStyle,
        isConnectable,
        editNode,
        onHover,
    } = props;

    const onClickEdit = () => {
        editNode(
            nodeData?.type,
            props?.id,
            NodeSubType.MULTI_ACTION,
            nodeData?.nodeIndex,
            nodeData?.automationChannel
        );
    };

    const descriptions = (nodeData?.description as any[]) ?? [];

    return (
        <ActionNodeStyle
            subType={NodeSubType.MULTI_ACTION}
            hasChildNodes={false}
        >
            {nodeData?.description && (
                <Handle
                    id="target"
                    type="target"
                    position={Position.Left}
                    style={leftHandlerStyle}
                    isConnectable={isConnectable}
                />
            )}
            <NodeStyle
                className="customNode flex--column"
                onMouseEnter={() => onHover(props?.id, NodeSubType.MULTI_ACTION)}
                onMouseLeave={() => onHover(null, undefined)}
                nodeColor={backgroundBasedOnSubType(NodeSubType.MULTI_ACTION)}
                onClick={onClickEdit}
                gap={"0px"}
            >
                {/* Header */}
                <div className="customNode__header flex justify-content-between align-items-center">
                    <div className="flex">
                        <span
                            className="customNode__header__icon icon-wrapper"
                            style={{ borderRadius: "4px" }}
                        >
                            {IconMapping(NodeSubType.MULTI_ACTION, nodeData?.automationChannel || "whatsapp")}
                        </span>
                        <NodeIdHeader nodeId={nodeData?.nodeId} />
                        <div style={{ width: 15 }} />
                        <span className="customNode__header__title">
                            {nodeData?.title || "Ticket actions"}
                        </span>
                    </div>
                </div>

                {/* Body - action entries */}
                <div className="customNode__body flex--column">
                    {descriptions.map((desc: Record<string, string[]>, i: number) =>
                        Object.entries(desc).map(([label, values]) => {
                            const isLabels = label.toLowerCase() === "labels";
                            const isCustomerTags = label.toLowerCase().includes("customer tags");
                            const isPriority = label.toLowerCase().includes("priority");
                            const isAssignTo = label.toLowerCase().includes("assign to");

                            return (
                                <React.Fragment key={`${i}-${label}`}>
                                    <ActionDescriptionRow className="customNode__body__description">
                                        <div
                                            className="flex justify-content-between align-items-center"
                                            style={{ width: "100%" }}
                                        >
                                            <BodyCaption color={COLORS.content.placeholder}>{label}</BodyCaption>
                                            <div className="flex align-items-center" style={{ gap: "8px" }}>
                                                {isPriority && values[0] && (
                                                    <PriorityDot color={getPriorityColor(values[0])} />
                                                )}
                                                {isAssignTo ? (
                                                    <div className="flex align-items-center" style={{ gap: "8px" }}>
                                                        <Avatar
                                                            type={AVATAR_TYPES.INITIALS}
                                                            name={values[0]}
                                                            size={SIZE.SMALL}
                                                            background={AVATAR_BACKGROUND.DARK}
                                                        />
                                                        <BodySecondary
                                                            color={COLORS.content.primary}
                                                            style={{ fontWeight: 500, fontSize: "14px" }}
                                                        >
                                                            {truncate(values[0], 12)}
                                                        </BodySecondary>
                                                    </div>
                                                ) : isLabels ? (
                                                    <TagValueList values={values} maxChars={12} />
                                                ) : isCustomerTags ? (
                                                    <TagValueList values={values} maxChars={10} />
                                                ) : (
                                                    <BodySecondary
                                                        color={COLORS.content.primary}
                                                        style={{ fontWeight: 500, fontSize: "14px" }}
                                                    >
                                                        {values.join(", ")}
                                                    </BodySecondary>
                                                )}
                                            </div>
                                        </div>
                                    </ActionDescriptionRow>
                                    {i < descriptions.length - 1 && <ActionSeparator />}
                                </React.Fragment>
                            );
                        })
                    )}
                    {descriptions.length < 6 && (
                        <AddActionButton
                            style={{ marginTop: descriptions.length === 0 ? "0px" : "16px" }}
                        >
                            Add an action
                        </AddActionButton>
                    )}
                </div>

                {/* Footer */}
                <div className="customNode__footer">
                    <div className="customNode__footer__next-step">Next step</div>
                </div>
            </NodeStyle>

            <Handle
                type="source"
                position={Position.Right}
                id="source"
                style={{ ...leftHandlerStyle, ...rightHandlerStyle }}
                isConnectable={isConnectable}
            />
        </ActionNodeStyle>
    );
};

export default MacroMultiActionNode;
